export const HEAVY_VISUAL_THRESHOLD = 1_000;
export const CLASSIFICATION_REPRESENTATIVE_CAP = 1_200;

export type ClassificationPageMetric = {
  pageId: string;
  pageNumber: number;
  confidence: number;
  margin: number;
  top1Category?: string;
};

/**
 * Selects actual pages that retain each contiguous window's center plus confidence and margin
 * extrema. Extra capacity is filled with evenly distributed pages so the
 * sampled visuals remain representative without exceeding the fixed mark cap.
 */
export function selectRepresentativeClassificationMetrics(
  classificationMetrics: ClassificationPageMetric[],
): ClassificationPageMetric[] {
  if (classificationMetrics.length <= CLASSIFICATION_REPRESENTATIVE_CAP) {
    return classificationMetrics.slice();
  }

  const selectedIndices = new Set<number>([0, classificationMetrics.length - 1]);
  const windowCount = Math.floor((CLASSIFICATION_REPRESENTATIVE_CAP - 2) / 5);

  for (let windowIndex = 0; windowIndex < windowCount; windowIndex += 1) {
    const startIndex = Math.floor((windowIndex * classificationMetrics.length) / windowCount);
    const endIndex = Math.floor(((windowIndex + 1) * classificationMetrics.length) / windowCount);
    selectWindowRepresentatives(classificationMetrics, startIndex, endIndex).forEach((index) => selectedIndices.add(index));
  }

  const remainingIndices = classificationMetrics.flatMap((_, index) => (selectedIndices.has(index) ? [] : [index]));
  const fillCount = Math.min(CLASSIFICATION_REPRESENTATIVE_CAP - selectedIndices.size, remainingIndices.length);

  for (let fillIndex = 0; fillIndex < fillCount; fillIndex += 1) {
    selectedIndices.add(remainingIndices[Math.floor((fillIndex * remainingIndices.length) / fillCount)]);
  }

  return [...selectedIndices]
    .sort((firstIndex, secondIndex) => firstIndex - secondIndex)
    .map((index) => classificationMetrics[index]);
}

export function getClassificationScatterPageDomain(visibleClassificationCount: number): [number, number] {
  return [0, visibleClassificationCount + 1];
}

export function isHugeClassificationVisual(visibleClassificationCount: number): boolean {
  return visibleClassificationCount >= HEAVY_VISUAL_THRESHOLD;
}

export function isHugeLineageVisual(visibleLineageLeafCount: number, reconstructedEntityCount: number): boolean {
  return (
    visibleLineageLeafCount >= HEAVY_VISUAL_THRESHOLD || reconstructedEntityCount >= HEAVY_VISUAL_THRESHOLD
  );
}

function selectWindowRepresentatives(
  classificationMetrics: ClassificationPageMetric[],
  startIndex: number,
  endIndex: number,
): number[] {
  const representatives = {
    minimumConfidence: startIndex,
    maximumConfidence: startIndex,
    minimumMargin: startIndex,
    maximumMargin: startIndex,
  };

  for (let index = startIndex + 1; index < endIndex; index += 1) {
    const metric = classificationMetrics[index];
    if (metric.confidence < classificationMetrics[representatives.minimumConfidence].confidence) {
      representatives.minimumConfidence = index;
    }
    if (metric.confidence > classificationMetrics[representatives.maximumConfidence].confidence) {
      representatives.maximumConfidence = index;
    }
    if (metric.margin < classificationMetrics[representatives.minimumMargin].margin) {
      representatives.minimumMargin = index;
    }
    if (metric.margin > classificationMetrics[representatives.maximumMargin].margin) {
      representatives.maximumMargin = index;
    }
  }

  const centralIndex = startIndex + Math.floor((endIndex - startIndex - 1) / 2);
  return [
    centralIndex,
    representatives.minimumConfidence,
    representatives.maximumConfidence,
    representatives.minimumMargin,
    representatives.maximumMargin,
  ];
}
